/**
 * 다른 계정 추가 페이지
 * 
 * @description
 * 사용자가 아이디와 비밀번호를 입력하여 다른 계정을 추가하고 기존 계정과 연결할 수 있는 페이지입니다.
 * 
 * @features
 * - 1단계: ID 입력 및 존재 여부 확인
 * - 2단계: Password 입력 및 계정 연결
 * - 기존 계정과 연결
 * - 연결 완료 확인 후 홈으로 이동
 * 
 * @api
 * - POST /user/check-login-id (ID 존재 여부 확인)
 * - POST /user/login (연결할 계정 인증)
 * - GET /user/profile (연결할 계정 정보 조회)
 * 
 * @routes
 * - /settings/add-account (현재 페이지)
 * - /settings (설정 페이지로 돌아가기)
 * - /signup (회원가입)
 */

import React, { useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button, AlertDialog, Input } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useUser } from '@/data/queries/useUser';
import { convertRoleNumberToString, resolveLicenseFromProfile } from '@/utils/roleUtils';
import { saveLinkedAccountSession } from '@/utils/linkedAccountSessions';

const ACCESS_TOKEN_KEY = 'medicrew_access_token';
const REFRESH_TOKEN_KEY = 'medicrew_refresh_token';
const LINKED_ACCOUNTS_KEY = 'medicrew_linked_accounts';

/** 로그인 ID — 한글·전각 등 비ASCII 입력 제거(영문·숫자·일부 기호만 허용) */
function sanitizeLoginIdInput(raw: string): string {
	return raw.replace(/[^A-Za-z0-9._@-]/g, '');
}

export default function AddAccount(): React.ReactElement {
	// ========== Hooks ==========
	
	const navigate = useNavigate();
	const { t } = useTranslation('auth');
	const { user, login: loginToAuth } = useAuth();
	const { login: loginApi, getProfile } = useUser();

	/** GNB+푸터 레이아웃 안에서 문서(body) 스크롤 방지 */
	useLayoutEffect(() => {
		const html = document.documentElement;
		const body = document.body;
		const prevHtml = html.style.overflow;
		const prevBody = body.style.overflow;
		html.style.overflow = 'hidden';
		body.style.overflow = 'hidden';
		return () => {
			html.style.overflow = prevHtml;
			body.style.overflow = prevBody;
		};
	}, []);

	// ========== State 관리 ==========
	
	/**
	 * 계정 연결 폼 데이터
	 * @field loginId - 연결할 계정의 아이디
	 * @field password - 연결할 계정의 비밀번호
	 */
	const [formData, setFormData] = useState<{
		loginId: string;
		password: string;
	}>({
		loginId: '',
		password: '',
	});

	/**
	 * 계정 연결 진행 중 상태
	 * @description 계정 연결 API 호출 중 버튼 비활성화
	 */
	const [isLoading, setIsLoading] = useState<boolean>(false);

	/**
	 * AlertDialog 상태
	 * @field isOpen - 팝업 열림 여부
	 * @field message - 표시할 메시지
	 */
	const [alertState, setAlertState] = useState<{
		isOpen: boolean;
		message: string;
		/** 확인 클릭 시에만 실행 (성공 후 홈 이동 등) */
		onConfirm?: () => void;
	}>({
		isOpen: false,
		message: '',
	});

	// ========== Helper 함수 ==========
	
	/**
	 * AlertDialog 표시 함수
	 * @param message - 표시할 메시지
	 */
	const showAlert = useCallback((message: string, options?: { onConfirm?: () => void }) => {
		setAlertState({ isOpen: true, message, onConfirm: options?.onConfirm });
	}, []);

	/**
	 * AlertDialog 닫기 함수
	 */
	const closeAlert = useCallback(() => {
		setAlertState({ isOpen: false, message: '', onConfirm: undefined });
	}, []);

	// ========== 계정 연결 처리 ==========
	
	/**
	 * 계정 연결 핸들러
	 * 
	 * @description
	 * ID와 비밀번호를 입력하여 다른 계정을 추가하고 기존 계정과 연결
	 * 
	 * @param e - 폼 제출 이벤트
	 * 
	 * @validation
	 * 1. 아이디 입력 여부 확인 → "아이디를 입력해주세요."
	 * 2. 비밀번호 입력 여부 확인 → "비밀번호를 입력해주세요."
	 * 
	 * @api
	 * - POST /user/login
	 * - GET /user/profile
	 * - 연결 계정 세션을 로컬에 저장
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// ========== 1단계: 유효성 검사 ==========
		
		// 아이디 입력 여부 확인
		if (!formData.loginId || formData.loginId.trim() === '') {
			showAlert(t('addAccount.alertLoginIdRequired'));
			return;
		}
		
		// 비밀번호 입력 여부 확인
		if (!formData.password || formData.password.trim() === '') {
			showAlert(t('addAccount.alertPasswordRequired'));
			return;
		}

		// ========== 2단계: 계정 연결 API 호출 ==========
		
		setIsLoading(true);

		// 현재 로그인 세션 보관 (연결된 계정 목록에 추가하기 위해)
		const savedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
		const savedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		const savedUserId = user?.userId;

		try {
			// 1. 연결할 계정으로 로그인 → 해당 계정의 프로필(role 등) 조회용 토큰 획득
			const loginResult = await loginApi({ loginId: formData.loginId.trim(), password: formData.password });
			localStorage.setItem(ACCESS_TOKEN_KEY, loginResult.accessToken);
			if (loginResult.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, loginResult.refreshToken);

		// 2. 연결할 계정의 실제 role/organization 조회
		const profile = await getProfile();
		const role = convertRoleNumberToString(profile.role);
		const userLicense = resolveLicenseFromProfile(profile);
		const organizationId = profile.organizationId != null ? String(profile.organizationId) : null;
		const organizationName = profile.organizationName || '';
		const displayName = (profile.realName && profile.realName.trim()) ? profile.realName.trim() : (profile.loginId || formData.loginId.trim());
		const accountId = `acc_${profile.userId}`;
		const newAccountUserId = profile.userId;

		// 3. 이전 계정 정보를 linkedAccountSession에 저장 (다른 계정인 경우에만)
		if (savedUserId && savedAccessToken && savedUserId !== newAccountUserId) {
			const savedAccountId = `acc_${savedUserId}`;
			saveLinkedAccountSession(savedAccountId, {
				accessToken: savedAccessToken,
				...(savedRefreshToken ? { refreshToken: savedRefreshToken } : {}),
			});
		}

		// 연결된 계정 전환 시 자동 로그인용 — 로그인 응답의 accessToken 보관( refresh는 선택 )
		if (loginResult.accessToken?.trim()) {
			saveLinkedAccountSession(accountId, {
				accessToken: loginResult.accessToken.trim(),
				...(loginResult.refreshToken?.trim()
					? { refreshToken: loginResult.refreshToken.trim() }
					: {}),
			});
		}

		const linkedAccount = {
			accountId,
			loginId: profile.loginId || formData.loginId.trim(),
			displayName,
			email: profile.email || '',
			profileImageUrl: profile.profileImageUrl,
			phoneNumber: profile.phoneNumber?.trim() ?? '',
			affiliation: profile.affiliation?.trim() ?? '',
			position: profile.position?.trim() ?? '',
			department: profile.department?.trim() ?? '',
			organizationId,
			organizationName,
			role,
			license: userLicense,
			isActive: true,
		};

		// 기존 계정이 있는지 확인하고 업데이트 또는 추가
		const existingAccountIndex = user?.accounts?.findIndex(
			acc => acc.accountId === accountId || acc.loginId === (profile.loginId || formData.loginId.trim())
		);

		let linkedAccounts;
		if (existingAccountIndex !== undefined && existingAccountIndex >= 0) {
			// 기존 계정 업데이트: 토큰 갱신 및 활성화
			linkedAccounts = [
				...(user?.accounts ?? []).map((acc, idx) =>
					idx === existingAccountIndex
						? { ...linkedAccount }
						: { ...acc, isActive: false }
				),
			];
		} else {
			// 새 계정 추가
			linkedAccounts = [
				...(user?.accounts ?? []).map(acc => ({ ...acc, isActive: false })),
				linkedAccount,
			];
		}
		localStorage.setItem(LINKED_ACCOUNTS_KEY, JSON.stringify(linkedAccounts));

		// 4. 새 계정으로 전환 (b계정 토큰을 현재 세션에 유지하고 AuthContext 업데이트)
		const updatedUser = {
			...user!,
			userId: newAccountUserId,
			name: displayName,
			email: profile.email || '',
			profileImageUrl: profile.profileImageUrl,
			isAuthenticated: true,
			currentAccount: {
				accountId,
				loginId: profile.loginId || formData.loginId.trim(),
				displayName,
				email: profile.email || '',
				profileImageUrl: profile.profileImageUrl,
				phoneNumber: profile.phoneNumber?.trim() ?? '',
				affiliation: profile.affiliation?.trim() ?? '',
				position: profile.position?.trim() ?? '',
				department: profile.department?.trim() ?? '',
				organizationId,
				organizationName,
				role,
				license: userLicense,
			},
			accounts: linkedAccounts,
		};
		
		loginToAuth(updatedUser);

			// 로그인 성공 시 홈으로 이동 (일반 로그인과 동일)
			navigate(ROUTES.HOME);
		} catch (error: any) {
			showAlert(error.message || t('addAccount.alertError'));
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsLoading(false);
		}
	};

	return (
		<div className="flex flex-col items-center justify-center px-4 py-3 sm:px-6 sm:py-4 box-border min-h-[calc(100dvh-9rem)] max-h-[calc(100dvh-9rem)] overflow-y-auto overscroll-y-contain">
			{/* Add Account Card — AppLayout 내 문서 스크롤 잠금 + 이 영역만 필요 시 스크롤 */}
			<div className="w-full max-w-[460px] bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
				{/* Header: Logo & Title */}
				<div className="text-center mb-5 sm:mb-6 shrink-0">
					<h1 className="text-lg sm:text-xl font-semibold text-brand-500 mb-1 sm:mb-2">
						{t('login')}
					</h1>
					<p className="text-gray-500 text-xs sm:text-sm leading-snug">
						{t('welcomeMessage')}
					</p>
				</div>

				{/* 로그인 폼 */}
				<form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3 sm:gap-4 min-h-0">
					{/* ID 입력 */}
					<div>
						<label htmlFor="loginId" className="block mb-1.5 sm:mb-2 text-sm font-semibold text-gray-900">
							{t('idLabel')}
						</label>
						<Input
							type="text"
							id="loginId"
							lang="en"
							autoCapitalize="off"
							autoCorrect="off"
							spellCheck={false}
							autoComplete="username"
							className="bg-gray-50 focus:bg-white border-transparent focus:border-blue-500 rounded-xl"
							placeholder={t('addAccount.loginIdPlaceholder')}
							value={formData.loginId}
							onChange={(e) => setFormData((prev) => ({ ...prev, loginId: sanitizeLoginIdInput(e.target.value) }))}
							required
						/>
					</div>

					{/* Password 입력 */}
					<div>
						<label htmlFor="password" className="block mb-1.5 sm:mb-2 text-sm font-semibold text-gray-900">
							{t('passwordLabel')}
						</label>
						<Input
							type="password"
							id="password"
							autoComplete="current-password"
							className="bg-gray-50 focus:bg-white border-transparent focus:border-blue-500 rounded-xl"
							placeholder={t('addAccount.passwordPlaceholder')}
							value={formData.password}
							onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
							required
						/>
					</div>

					{/* Actions */}
					<div className="flex flex-col gap-2 sm:gap-3 mt-1 sm:mt-2">
						<Button
							type="submit"
							className="w-full h-10 sm:h-11 rounded-full text-sm sm:text-base shadow-lg shadow-brand-100 active:scale-95"
							variant="primary"
							disabled={isLoading}
						>
							{isLoading ? '로그인 중...' : '로그인'}
						</Button>
						<Button
							type="button"
							onClick={() => navigate(-1)}
							className="w-full h-10 sm:h-11 rounded-full text-sm sm:text-base active:scale-95"
							variant="secondary"
							disabled={isLoading}
						>
							{t('cancel')}
						</Button>
					</div>
				</form>
			</div>

			{/* AlertDialog: 계정 연결 성공/실패 메시지 표시 */}
			<AlertDialog
				isOpen={alertState.isOpen}
				onClose={closeAlert}
				message={alertState.message}
				onConfirm={alertState.onConfirm}
			/>
		</div>
	);
}